<template>
  <Modal modalType="ImageExport" mayCancel="true" v-on:cancel="onCancel">
    <h2>Save Image</h2>
    <form class="SaveImage_Form PForm" v-on:click.stop novalidate>
      <p class="SaveImage_Form_Section">
        <label class="m-inline" for="transparentBackground"
          >Transparent background</label
        >
        <input
          v-model="transparentBackground"
          type="checkbox"
          id="transparentBackground"
        />
      </p>
      <p class="SaveImage_Form_Section">
        <label class="m-inline" for="scaleFactor">Scale factor</label>
        <input
          v-model="scaleFactor"
          type="number"
          min="0.01"
          step="0.5"
          id="scaleFactor"
        />
      </p>
      <p>Size: {{ computedSize.x }}x{{ computedSize.y }}</p>
      <p class="InlineWarning" v-if="sizeWarning">
        At this size, your export will take several minutes.
      </p>
      <p v-if="sizeWarning">
        <button class="WBMBigButton" v-on:click.prevent="onMakeSmaller">
          Shrink to biggest size that exports quickly
        </button>
      </p>
    </form>
    <div class="ButtonRow-FlexSpaceBetween" v-if="progress !== null">
      <progress :value="progress" max="1">
        {{ Math.round(progress * 100) }}%
      </progress>
    </div>
    <div class="ButtonRow-FlexSpaceBetween">
      <button class="WBMBigButton" v-on:click="onCancel">Cancel</button>
      <button class="WBMBigButton m-primary" ref="downloadButton">
        Download
      </button>
    </div>
  </Modal>
</template>

<script lang="ts">
import Vector from "victor";
import { Ref, Component, Emit } from "vue-property-decorator";
import Modal from "@/components/Modal.vue";
import renderAndDownloadImage from "@/utils/renderAndDownloadImage";
import FileSaver from "file-saver";
import WBComponent from "@/WBComponent";
import { ModalMutations } from "@/store/modules/modal";

// 16384 is the magic number at which the browser can't composite quickly.

@Component({ components: { Modal } })
export default class ImageExportModal extends WBComponent {
  @Ref() downloadButton!: HTMLElement;
  transparentBackground = false;
  progress: number | null = null;

  scaleFactor = "1";

  get drawingSize(): Vector {
    return (
      this.wbcontroller?.drawing.findBoundingRect().size || new Vector(0, 0)
    );
  }

  get sizeWarning(): boolean {
    return this.computedSize.x > 16384 || this.computedSize.y > 16384;
  }

  get computedSize(): Vector {
    const size = this.drawingSize
      .clone()
      .multiplyScalar(parseFloat(this.scaleFactor));
    return new Vector(Math.ceil(size.x), Math.ceil(size.y));
  }

  mounted() {
    this.scaleFactor = "1";
    this.downloadButton.addEventListener("click", (e) => {
      e.stopPropagation();
      this.downloadImage();
    });
  }

  downloadImage() {
    if (!this.wbcontroller) return;
    renderAndDownloadImage(
      this.wbcontroller,
      parseFloat(this.scaleFactor),
      this.transparentBackground,
      (progress) => {
        this.progress = progress;
      },
      (url) => {
        FileSaver.saveAs(url, `${this.state.whiteboard.name}.png`);
        ModalMutations.closeModals(this.$store.commit);
      }
    );
  }

  onMakeSmaller() {
    if (this.drawingSize.x > 16384) {
      this.scaleFactor = `${16384 / this.drawingSize.x}`;
    }
    if (this.drawingSize.y > 16384) {
      this.scaleFactor = `${16384 / this.drawingSize.y}`;
    }
  }

  @Emit("cancel")
  onCancel() {
    ModalMutations.closeModals(this.$store.commit);
  }
}
</script>

<style scoped lang="scss">
input[type="number"] {
  max-width: 4em;
}

.SaveImage_Form_Section {
  line-height: 1em;
  margin-top: 0;
}
</style>
